import { connect } from 'react-redux';
import { compose, withHandlers, withStateHandlers, branch } from 'recompose';
import { translator } from 'mage-i18n';

import { CAPS } from '@/constants';
import { withChainedCapabilitiesBeforeNext } from '@/flow/common';
import { getStakeholders, merchantRegion } from '@/selectors/merchant';
import { shouldCollectDocuments } from '@/flow/configs/selectors/documents';

import {
  getMaxFromRoleConstraintsInProps,
  getMinFromRoleConstraintsInProps,
  initialState,
  onAddStakeholder,
  onBlurStakeholder,
  onChangeRoles,
  onChangeStakeholder,
  onRemoveStakeholder,
  onRemoveAllStakeholders,
  onNextWrapper,
  onTouchPersonalId,
} from './utils/stakeholderContainer';
import {
  getStakeholdersBasedOnRoles,
  inversedGetStakeholdersBasedOnRoles,
  updateRoleConstraintsBasedOnStakeholders,
} from './utils/stakeholderUtils';

export const mergeProps = (extraProps = {}) => (
  { stakeholdersNotHandledByThisContainer, ...stateProps },
  dispatchProps,
  props,
) => {
  const {
    onNext: parentOnNext,
    overrideStakeholders = false,
    roleConstraints: defaultRoleConstraints,
    ...ownProps
  } = {
    ...extraProps,
    ...props,
  };

  const actualRoleConstraints = overrideStakeholders
    ? updateRoleConstraintsBasedOnStakeholders([], defaultRoleConstraints)
    : updateRoleConstraintsBasedOnStakeholders(
        stakeholdersNotHandledByThisContainer,
        defaultRoleConstraints,
      );

  return {
    ...stateProps,
    ...dispatchProps,
    ...extraProps,
    ...ownProps,
    roleConstraints: actualRoleConstraints,
    min: getMinFromRoleConstraintsInProps({
      roleConstraints: actualRoleConstraints,
      ...extraProps,
      ...ownProps,
    }),
    max: getMaxFromRoleConstraintsInProps({
      roleConstraints: actualRoleConstraints,
      ...extraProps,
      ...ownProps,
    }),
    onNext: ({ payload: { body = [], ...payloadRest } = {}, hasChanged = true, ...rest }) =>
      parentOnNext({
        payload: {
          ...payloadRest,
          body: overrideStakeholders ? body : body.concat(stakeholdersNotHandledByThisContainer),
        },
        hasChanged,
        scrollBeforeNext: true,
        ...rest,
      }),
  };
};

export default (
  cap,
  { capabilityCallback, extraProps, stakeholdersSelector = getStakeholders } = {},
) =>
  compose(
    connect(state => ({
      merchantRegion: merchantRegion(state),
      countryCollectsDocuments: shouldCollectDocuments(state),
    })),
    branch(
      ({ countryCollectsDocuments }) => countryCollectsDocuments,
      withChainedCapabilitiesBeforeNext(
        {
          updatedStakeholders: cap,
          refreshDocuments: CAPS.V2_DOC_LIST,
        },
        {
          forceCapabilitiesOrder: ['updatedStakeholders', 'refreshDocuments'],
          callback: capabilityCallback,
        },
      ),
      withChainedCapabilitiesBeforeNext(
        {
          updatedStakeholders: cap,
        },
        {
          forceCapabilitiesOrder: ['updatedStakeholders'],
          callback: capabilityCallback,
        },
      ),
    ),
    connect(
      (state, props) => {
        const { defaultRole, cantHaveRoles = [], overrideStakeholders = false } = {
          ...extraProps,
          ...props,
        };

        return {
          t: translator(state),
          defaultStakeholders: getStakeholdersBasedOnRoles(
            stakeholdersSelector(state),
            [defaultRole],
            cantHaveRoles,
          ),
          stakeholdersNotHandledByThisContainer: overrideStakeholders
            ? []
            : inversedGetStakeholdersBasedOnRoles(
                stakeholdersSelector(state),
                [defaultRole],
                cantHaveRoles,
              ),
          onNextWrapper,
        };
      },
      {},
      mergeProps(extraProps),
    ),
    withStateHandlers(initialState, {
      onAddStakeholder,
      onRemoveStakeholder,
      onChangeStakeholder,
      onBlurStakeholder,
      onChangeRoles,
      onRemoveAllStakeholders,
      onTouchPersonalId,
    }),
    withHandlers({
      onNext: props => ({ payload = {}, ...rest } = {}) =>
        props.onNext({ payload: { region: props.merchantRegion, ...payload }, ...rest }),
    }),
    connect((state, { onNext, stakeholders, onChangeStakeholder }) => ({
      onNextWrapper: onNextWrapper(onNext, stakeholders, onChangeStakeholder),
    })),
  );
